import highlightWidget from 'components/Dashboard/apiCalls/callHighlightWidget';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useEventListFunctions from 'components/Dashboard/LiveFromParliament/useEventListFunctions';
import useGetRealTimeEvents from 'components/Dashboard/ParliamentLive/useGetRealtimeEvents';
import time from 'utils/time';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);

const useEmbedSpecificContentApiCall = (props) => {
  const { includeBrandingProperty, contentState, initialDateState, results, topic } = props;
  const { setInitialDate } = initialDateState ?? {};
  const { content, setContent } = contentState ?? {};

  const { generalApiCall } = useGeneralApiCall();
  const { callEvents } = useEventListFunctions();
  const { getRealTimeEvent } = useGetRealTimeEvents();

  const transformParliamentaryRecordApi = ({ contentType }) => {
    if (contentType) {
      switch (contentType) {
        case 'QuestionAndAnswer':
          return 'question-and-answer';
        case 'ScotlandCountrySpecificParliamentaryContribution':
          return 'country-specific-parliamentary-record/scotland';
        case 'WalesCountrySpecificParliamentaryContribution':
          return 'country-specific-parliamentary-record/wales';
        case 'ScotlandQuestionAndAnswer':
          return 'country-specific-question-and-answer/scotland';
        case 'WalesQuestionAndAnswer':
          return 'country-specific-question-and-answer/wales';
        case 'NorthernIrelandQuestionAndAnswer':
          return 'country-specific-question-and-answer/northernireland';
        case 'Edm':
          return 'edm';
        case 'SeneddCommitteeTranscriptContribution':
          return 'senedd-committee-transcript';
        default:
          return 'parliamentaryrecord';
      }
    }
  };

  const callSpecificEvent = async ({ eventId, source }) => {
    try {
      let initialDateEvent;
      let eventBodies;
      const eventItem = await getRealTimeEvent({ source, eventId });
      if (!!eventItem) {
        const dateUK = dayjs().utc();
        const normalizeEventDate = time(eventItem?.actualStartDateTime).utc();
        initialDateEvent = dateUK.isSame(normalizeEventDate, 'day') ? null : normalizeEventDate;
        const eventQuery = await callEvents({ source, eventId, setInitialDate, specificDate: initialDateEvent });
        if (!!eventQuery) {
          eventBodies = eventQuery?.hits?.map((item) => ({ ...item, text: item.body }));
          const currentNew = { eventBodies, eventItem };
          return currentNew;
        }
      }
      return null;
    } catch (error) {}
  };

  const specificContentApiCall = async ({ item, source }) => {
    try {
      if (item) {
        const TransformApi = (type) => {
          switch (type) {
            case 'Legislation':
              return `${item.contentType.toLowerCase()}legislation`;
            case 'ParliamentaryRecord':
              return transformParliamentaryRecordApi({ contentType: item?.contentType });
            case 'CommonsLibraryArticle':
              return 'commons-library-article';
            default:
              return type.toLowerCase();
          }
        };

        const FirstChildToHighlight = (type) => {
          switch (type) {
            case 'Legislation':
              return item.contentType === 'Primary' ? 'billTitleHighlighted' : 'titleHighlighted';
            case 'KeyUpdate':
              return 'subjectHighlighted';
            case 'ParliamentaryRecord':
              return item.contentType.includes('QuestionAndAnswer') ? 'questionForHighlighted' : 'titleHighlighted';
            default:
              return 'titleHighlighted';
          }
        };

        const SecondChildToHighlight = (type, currentNew) => {
          switch (type) {
            case 'Legislation':
              return item.contentType === 'Primary' ? 'summary' : 'explanatoryNote';
            case 'ParliamentaryRecord':
              return item.contentType.includes('QuestionAndAnswer')
                ? 'questionText'
                : `${currentNew?.content ? 'content' : 'body'}`;
            default:
              return 'body';
          }
        };

        setContent(!!content ? { ...content, isLoading: true } : { isLoading: true });
        let newData;
        if (item?.type === 'RealtimeUtterance') {
          const currentNew = await callSpecificEvent({ eventId: item?.eventId, source });
          if (!!currentNew) {
            newData = { ...currentNew };
          }
        } else {
          const pathname = `/api/${TransformApi(item.type)}/${item.id}${item?.type === 'KeyUpdate' && !includeBrandingProperty ? '?removeRelativeLinks=true' : ''}`;

          const currentNew = await generalApiCall({
            method: 'get',
            pathname,
            requestSource: source,
            notShowErrorMessage: true,
          });
          const { objectId, objectType } = currentNew;
          let objectTypeToApi = item.contentType?.includes('QuestionAndAnswer') ? 'WrittenQuestion' : objectType;
          let handpickedTopic = item.type === 'KeyUpdate' && results?.handPickedTopic;
          let highlightedData;
          if (!handpickedTopic) {
            highlightedData = await highlightWidget(
              objectTypeToApi,
              objectId,
              item?.keywordListId ?? topic?.id,
              includeBrandingProperty
            );
          }

          let highlightedAnswer;
          if (currentNew.questionHasAnswer) {
            highlightedAnswer = await highlightWidget('WrittenAnswer', objectId, item?.keywordListId ?? topic?.id);
          }
          newData = { ...currentNew };

          if (!!highlightedData) {
            if (!!highlightedData.highlightedTitle) {
              newData[FirstChildToHighlight(item.type)] = highlightedData.highlightedTitle;
            }
            if (!!highlightedData.highlightedHtmlBody) {
              newData[SecondChildToHighlight(item.type, newData)] = highlightedData.highlightedHtmlBody;
            }
          }

          //AE: THIS PART ONLY IS NECESSARY FOR QUESTIONS AND ANSWERS
          if (!!highlightedAnswer) {
            if (!!highlightedAnswer.highlightedHtmlBody) {
              newData['answerText'] = highlightedAnswer.highlightedHtmlBody;
            }
          }
        }
        if (!!newData) {
          setContent({
            item: content?.item,
            isLoading: false,
            currentNew: newData,
            contributionId: item.contentType === 'ParliamentaryContribution' ? item.contributionId : null,
          });
        }
      }
    } catch (error) {}
  };

  return { specificContentApiCall };
};

export default useEmbedSpecificContentApiCall;
